import { Injectable } from '@angular/core';
import { distinctUntilChanged, Observable, shareReplay, Subject } from 'rxjs';
import { PromptResponse, ResponseCommand, SessionInitCommand } from '@panaya/ai-chat/domain';

/**
 * Service that provides thread_id values extracted from prompt responses.
 * This supports servers that return session_id in prompt responses instead of init responses.
 *
 * The conversation service automatically calls `notifyThreadId()` when it receives a session_init command.
 * No configuration or providers are needed - this works automatically for both legacy and new servers.
 */
@Injectable({ providedIn: 'root' })
export class AiChatThreadIdProviderService {
    private readonly threadIdSubject$ = new Subject<string>();

    /**
     * Observable stream of thread_ids extracted from prompt responses.
     * Emits when a session_init command is found in a prompt response.
     * Automatically deduplicates emissions to prevent redundant updates.
     */
    public readonly threadIdFromPrompt$: Observable<string> = this.threadIdSubject$
        .asObservable()
        .pipe(distinctUntilChanged(), shareReplay(1));

    /**
     * Notify that a thread_id was received from a prompt response.
     * Called internally by the conversation service when processing prompt responses.
     * External callers should not need to use this method.
     */
    public notifyThreadId(threadId: string): void {
        this.threadIdSubject$.next(threadId);
    }
}

/**
 * Type guard to check if a command is a SessionInitCommand
 */
function isSessionInitCommand(command: ResponseCommand): command is SessionInitCommand {
    return command.type === 'session_init';
}

/**
 * Extracts the session_id from a prompt response if present
 */
export function extractThreadIdFromResponse(response: PromptResponse): string | null {
    const sessionInitCommand = response.commands?.find(isSessionInitCommand);
    return sessionInitCommand?.session_id ?? null;
}
